import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { SeenState } from "../models/SeenState";
import { useMatter } from "./MatterContext";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";
import { SeenStateService } from "../services/SeenStateService";

interface SeenStateContextProps {
  recordSeenState: (input: {
    eventId?: string;
    participantId?: string;
    fileId?: string;
    documentId?: string;
    discoveryRequestTaskId?: string;
  }) => Promise<void>;
}

const SeenStateContext = createContext<SeenStateContextProps | undefined>(
  undefined,
);

export const useSeenState = (): SeenStateContextProps => {
  const context = useContext(SeenStateContext);
  if (!context) {
    throw new Error("useSeenState must be used within a SeenStateProvider");
  }
  return context;
};

export const SeenStateProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();

  const recordSeenState = async ({
    eventId,
    participantId,
    fileId,
    documentId,
    discoveryRequestTaskId,
  }: {
    eventId?: string;
    participantId?: string;
    fileId?: string;
    documentId?: string;
    discoveryRequestTaskId?: string;
  }): Promise<void> => {
    if (!user || !currentMatterId) return;
    try {
      const targetType = eventId
        ? "event"
        : fileId
          ? "file"
          : documentId
            ? "document"
            : discoveryRequestTaskId
              ? "discoveryRequestTask"
              : "participant";
      await SeenStateService.createSeenState({
        targetType,
        targetId:
          eventId ||
          participantId ||
          fileId ||
          documentId ||
          discoveryRequestTaskId,
      });
    } catch (err) {
      console.error("Failed to record seen state:", err);
    }
  };

  return (
    <SeenStateContext.Provider
      value={{
        recordSeenState,
      }}
    >
      {children}
    </SeenStateContext.Provider>
  );
};
